// PreviewLanguageSelectModal.tsx
import React, { useState } from "react"
import { Button, Modal, Select } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons"
import { notify } from "shared/ui/Notifications/notify"
import { Project } from "stores/projectStore"
import { Variant, SelectedVariant } from "types/questionnaire.types"

/**
 * Props interface for the PreviewLanguageSelectModal component
 */
interface PreviewLanguageSelectModalProps {
  /** The project object */
  project: Project
  /** Array of variant objects */
  variants: Variant[]
  /** The currently selected variant object */
  selectedVariant: SelectedVariant
  /** Function to generate the preview */
  generatePreview: (languages: string[], variant: string, notes?: string[]) => Promise<void>
  /** Whether the button should be disabled */
  isDisabled?: boolean
  /** Function to retrieve the notes for the questionnaire */
  getQuestionnaireNotes?: () => string[]
}

/**
 * Modal component for language selection.
 *
 * @component
 * @param props - Component props
 * @returns The rendered PreviewLanguageSelectModal component
 */
const PreviewLanguageSelectModal: React.FC<PreviewLanguageSelectModalProps> = ({
  project,
  variants,
  selectedVariant,
  generatePreview,
  isDisabled = false,
  getQuestionnaireNotes,
}) => {
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState<boolean>(false)
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([])
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
  const [selectedNotes, setSelectedNotes] = useState<string[]>([])
  const [availableNotes, setAvailableNotes] = useState<string[]>([])

  /**
   * Handles the preview click by collecting languages from the selected variant
   * and opening the language selection modal.
   */
  const handlePreviewClick = (): void => {
    // Get unique notes from questionnaire
    if (getQuestionnaireNotes) {
      const notes = [...new Set(getQuestionnaireNotes())]
      setAvailableNotes(notes)
      setSelectedNotes(notes) // Default to all notes selected
    }

    let languages: string[] = []
    // Collect languages from the selected variant only
    if (variants && variants.length > 0) {
      if (!selectedVariant || !(selectedVariant as Variant).name) {
        notify.error(
          "Can't Generate Preview For Master If Variants Exist!",
          "Please select a variant to generate a preview."
        )
        return
      }
      // Get languages only from the selected variant
      const currentVariant = variants.find(
        (v) => v.name === (selectedVariant as Variant).name
      )
      if (currentVariant?.languages && currentVariant.languages.length > 0) {
        languages = [...currentVariant.languages]
      }
      // Include the design language if not already included
      if (
        project.Designlanguage &&
        !languages.includes(project.Designlanguage)
      ) {
        languages.push(project.Designlanguage)
      }
      if (languages.length === 0) {
        notify.error(
          "No languages available in the selected variant.",
          "Please check the variant languages."
        )
        return
      }
      setAvailableLanguages(languages)
      setSelectedLanguages(languages) // Default to all languages selected
      setIsLanguageModalOpen(true)
    } else {
      // No variants exist - show modal for notes selection if notes are available
      if (getQuestionnaireNotes) {
        const notes = [...new Set(getQuestionnaireNotes())]
        if (notes.length > 0) {
          setAvailableLanguages([project.Designlanguage])
          setSelectedLanguages([project.Designlanguage])
          setIsLanguageModalOpen(true)
          return
        }
      }
      // No variants and no notes - generate directly
      const masterLanguage: string = project.Designlanguage
      generatePreview([masterLanguage], "MASTER")
    }
  }

  /**
   * Handles the generate preview action with selected languages.
   */
  const handleGeneratePreview = (): void => {
    if (selectedLanguages.length === 0) {
      notify.error(
        "No languages selected",
        "Please select at least one language for the preview."
      )
      return
    }
    setIsLanguageModalOpen(false)
    // Always pass notes array if notes are available (empty array = exclude all notes)
    const notesToInclude = availableNotes.length > 0 ? selectedNotes : undefined
    if (variants && variants.length > 0) {
      // Variants exist; use selectedVariant
      generatePreview(selectedLanguages, (selectedVariant as Variant).name, notesToInclude)
    } else {
      // No variants; use MASTER
      generatePreview(selectedLanguages, "MASTER", notesToInclude)
    }
  }

  return (
    <>
      {/* The toolbar's one filled button */}
      <Button
        type="primary"
        icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        onClick={handlePreviewClick}
        disabled={isDisabled}
      >
        Preview
      </Button>
      <Modal
        title="Preview Options"
        open={isLanguageModalOpen}
        onCancel={() => setIsLanguageModalOpen(false)}
        footer={null}
        centered
      >
        <div className="flex flex-col space-y-4 pt-5">
          {/* Language Selection - Multi-select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Languages
            </label>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              placeholder="Select languages to include"
              value={selectedLanguages}
              onChange={(values: string[]) => setSelectedLanguages(values)}
              options={availableLanguages.map((lang) => ({
                value: lang,
                label: lang,
              }))}
            />
            <p className="text-xs text-gray-500 mt-1">
              Select multiple languages to show them side-by-side in the preview.
            </p>
          </div>

          {/* Notes Selection */}
          {availableNotes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Include Notes
              </label>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Select note types to include"
                value={selectedNotes}
                onChange={(values: string[]) => setSelectedNotes(values)}
                options={availableNotes.map((note) => ({
                  value: note,
                  label: note,
                }))}
              />
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <DataQButton
              type="secondary"
              onClick={() => setIsLanguageModalOpen(false)}
            >
              Cancel
            </DataQButton>
            <DataQButton
              type="primary"
              onClick={handleGeneratePreview}
            >
              Generate Preview
            </DataQButton>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default PreviewLanguageSelectModal
